import { has } from "lodash"

import { SelfAssessment } from "@api/schema"

type SelfAssessmentKeyList = {
  [key in SelfAssessment]: string
}

/**
 * Contains the i18n labels for display of self assessment status in a RangeSlider
 */
const rangeSliderProgressLabels: SelfAssessmentKeyList = {
  [SelfAssessment.Starting]: "selfassessment.progress.0",
  [SelfAssessment.MakingProgress]: "selfassessment.progress.25",
  [SelfAssessment.HalfFinished]: "selfassessment.progress.50",
  [SelfAssessment.AlmostFinished]: "selfassessment.progress.75",
  [SelfAssessment.Complete]: "selfassessment.progress.100ForRangeSlider",
}

/**
 * Builds a record of SelfAssessment keys and i18n translations that may be used in certain form elements,
 * e.g. RangeSlider.
 *
 * NOTE it would be nice to have an universal "translate a given enum with i18n keys", but it seems to be nearly impossible
 * due to (our restricted knowledge about) typescript.
 *
 * @param t A translation function like i18n.Translate
 * @returns A map/record of SelfAssessment keys and according i18n translations
 */
export const translatedRangeSliderProgressLabels = (t: (key: string) => string): { [key: number]: string } => {
  const ret: { [key: number]: string } = {}
  Object.entries(rangeSliderProgressLabels).forEach(([key, value]) => ret[Number.parseInt(key, 10)] = t(value))
  return ret
}

/**
 * Contains the i18n labels for display of self assessment status in a WorkflowBox
 */
export const workflowBoxProgressLabels: SelfAssessmentKeyList = {
  [SelfAssessment.Starting]: "selfassessment.progress.0",
  [SelfAssessment.MakingProgress]: "selfassessment.progress.25",
  [SelfAssessment.HalfFinished]: "selfassessment.progress.50",
  [SelfAssessment.AlmostFinished]: "selfassessment.progress.75",
  [SelfAssessment.Complete]: "selfassessment.progress.100ForWorkflowBox",
}

/**
 * Retrieves a valid i18n key for the given progress using the given key list.
 *
 * NOTE we expect SelfAssessment to be one of the 5 values (as defined in backend:src/Entity/Proposal/AbstractProposal.php),
 * but nevertheless we want to absolutely avoid having "wrong" display values, that's why we fallback to "unknown".
 *
 * @param progress A self assessment / progress value
 * @param keyList A self assessment / progress i18n key list
 * @returns A valid i18n key
 */
export const selfAssessmentKey = (progress: SelfAssessment, keyList: SelfAssessmentKeyList): string =>
  has(keyList, progress)
    ? keyList[progress]
    : "selfassessment.progress.unknown"

